import React, { useEffect, useState, useCallback } from "react";
import { Icon } from '@iconify/react';
import { Link, NavLink, useLocation } from "react-router-dom";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import { applicationTabs } from "../helper/applicationTabs";
import { useApplicationContext } from "../context/ApplicationContext";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { applyTheme, initTheme } from "../helper/themeHelper";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(localStorage.getItem('sidebarActive') === 'true');
  const { activeTabId, setActiveTabId } = useApplicationContext();
  let [mobileMenu, setMobileMenu] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation(); // Hook to get the current route
  const { auth, user } = useAuth();
  const [hasSubscription, setHasSubscription] = useState(false);
  const APP_TYPE = process.env.REACT_APP_APP_TYPE || 'dev';

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
        if (!user) return;
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
            if (response.ok) {
                const data = await response.json();
                setHasSubscription(!!data.subscription);
            }
        } catch (error) {
            console.error('Failed to check subscription:', error);
        }
    };

    checkSubscription();
  }, [user, auth]);

  // Fetch notifications logic
  const fetchNotifications = useCallback(async () => {
    try {
        const response = await auth.fetch(`${config.API_BASE_URL}/api/notifications`);
        
        if (response.ok) {
            const data = await response.json();
            setNotifications(data.notifications);
            setUnreadCount(data.unreadCount);
        }
    } catch (error) {
        console.error('Failed to fetch notifications', error);
    }
  }, [auth]);

  useEffect(() => {
    if (user) {
        fetchNotifications();
        // Poll every 60 seconds
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
        await auth.fetch(`${config.API_BASE_URL}/api/notifications/read-all`, {
            method: 'PUT'
        });
        fetchNotifications();
    } catch (error) {
        console.error('Failed to mark all read', error);
    }
  };

  // Helper to check permissions
  const hasPermission = (permission) => {
    if (!user) return false;
    if (permission === 'authenticated') return true;
    if (user.role === 'admin') return true;
    if (!user.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Initialize theme from local storage on mount
  useEffect(() => {
    initTheme();
  }, []);

  useEffect(() => {
    // Fetch settings for logo and theme
    fetch(`${config.API_BASE_URL}/api/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.logo_url) {
           const url = data.logo_url.startsWith('http') ? data.logo_url : `${config.API_BASE_URL}${data.logo_url}`;
           setLogoUrl(url);
        }
        if (data.site_title) document.title = data.site_title;
        if (data.theme_color) {
            applyTheme(data.theme_color);
        }
      })
      .catch(err => console.error('Failed to fetch settings:', err));

    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px"; // Collapse submenu
        }
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
        }
      }
    };

    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
            }
          }
        });
      });
    };

    // Open the submenu that contains the active route
    openActiveDropdown();

    // Cleanup event listeners on unmount
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname, hasSubscription]);

  let sidebarControl = () => {
    const newState = !sidebarActive;
    seSidebarActive(newState);
    localStorage.setItem('sidebarActive', newState);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to='/' className='sidebar-logo'>
            <img
              src={logoUrl || '/assets/images/logo.png'}
              alt='site logo'
              className='light-logo'
              onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/logo.png'; }}
            />
            <img
              src={logoUrl || '/assets/images/logo-light.png'}
              alt='site logo'
              className='dark-logo'
              onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/logo-light.png'; }}
            />
            <img
              src={logoUrl || '/assets/images/logo-icon.png'}
              alt='site logo'
              className='logo-icon'
              onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/logo-icon.png'; }}
            />
            {!sidebarActive && (
              <span className="ms-2 fw-bold" style={{
                background: 'linear-gradient(270deg, #24c5f7 0%, #0388d6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '24px',
                lineHeight: '1',
                display: 'inline-block'
              }}>
                Hubscroll
              </span>
            )}
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
            {hasPermission('dashboard') && (
            <li className='dropdown'>
              <Link to='#'>
                <Icon
                  icon='solar:home-smile-angle-outline'
                  className='menu-icon'
                />
                <span>Dashboard</span>
              </Link>
              <ul className='sidebar-submenu'>
                <li>
                  <NavLink
                    to='/index-1'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    Overview
                  </NavLink>
                </li>
                {hasPermission('admin_access') && (
                <>
                <li>
                  <NavLink
                    to='/control-tower'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    Control Tower
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/audit-logs'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    Audit Logs
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/contact-messages'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    Contact Messages
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/reservations'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    Reservations
                  </NavLink>
                </li>
                </>
                )}
              </ul>
            </li>
            )}

            <li className='sidebar-menu-group-title'>Application</li>
            
            {/* My Venues - Available to all authenticated users with active subscription, but hidden for admin app */}
            {hasSubscription && APP_TYPE !== 'admin' && (
            <li className='dropdown'>
              <Link to='#'>
                <Icon icon='mdi:store-outline' className='menu-icon' />
                <span>My Venues</span>
              </Link>
              <ul className='sidebar-submenu'>
                <li>
                  <NavLink
                    to='/my-venues'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    All Venues
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/add-venue'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""

                    }
                  >
                    <i className='ri-circle-fill circle-icon text-success-main w-auto' />
                    Add New Venue
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/reservations'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />
                    Reservations
                  </NavLink>
                </li>
              </ul>
            </li>
            )}

            <li>
              <NavLink
                to='/view-profile'
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon='solar:user-circle-outline' className='menu-icon' />
                <span>Profile Settings</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to='/tos'
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon='solar:document-text-outline' className='menu-icon' />
                <span>Terms of Service</span>
              </NavLink>
            </li>
            {hasPermission('content') && (
            <li>
              <NavLink
                to='/email'
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon='mage:email' className='menu-icon' />
                <span>Email</span>
              </NavLink>
            </li>
            )}


            {/* Application Tabs */}
            {applicationTabs.filter(tab => hasPermission(tab.permission)).filter(tab => APP_TYPE !== 'admin' || tab.id !== 'my-plan').map((tab) => (
              <li key={tab.id}>
                <Link
                  to='/applications'
                  onClick={() => setActiveTabId(tab.id)}
                  className={location.pathname === '/applications' && activeTabId === tab.id ? "active-page" : ""}
                >
                  <Icon icon={tab.icon} className='menu-icon' />
                  <span>{tab.title}</span>
                </Link>
              </li>
            ))}
            {hasPermission('content') && (
            <li>
              <NavLink
                to='/calendar-main'
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon='solar:calendar-outline' className='menu-icon' />
                <span>Calendar</span>
              </NavLink>
            </li>
            )}
            {hasPermission('content') && (
            <li>
              <NavLink
                to='/maps'
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon
                  icon='material-symbols:map-outline'
                  className='menu-icon'
                />
                <span>Maps</span>
              </NavLink>
            </li>
            )}













            {/* Settings Dropdown */}
            {hasPermission('admin_access') && (
            <li className='dropdown'>
              <Link to='#'>
                <Icon
                  icon='icon-park-outline:setting-two'
                  className='menu-icon'
                />
                <span>Settings</span>
              </Link>
              <ul className='sidebar-submenu'>
                <li>
                  <NavLink
                    to='/notification'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-warning-main w-auto' />{" "}
                    Notification
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/theme'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className='ri-circle-fill circle-icon text-danger-main w-auto' />{" "}
                    Website Settings
                  </NavLink>
                </li>
              </ul>
            </li>
            )}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
                <form className='navbar-search'>
                  <input type='text' name='search' placeholder='Search' />
                  <Icon icon='ion:search-outline' className='icon' />
                </form>
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {/* ThemeToggleButton */}
                <ThemeToggleButton />
                
                <div className='dropdown'>
                  <button
                    className='has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <Icon
                      icon='mage:email'
                      className='text-primary-light text-xl'
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-lg p-0'>
                    <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-0'>
                          Message
                        </h6>
                      </div>
                      <span className='text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center'>
                        05
                      </span>
                    </div>
                    <div className='max-h-400-px overflow-y-auto scroll-sm pe-4'>
                      <Link
                        to='#'
                        className='px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between'
                      >
                        <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                          <span className='w-40-px h-40-px rounded-circle flex-shrink-0 position-relative'>
                            <img
                              src='/assets/images/notification/profile-3.png'
                              alt=''
                            />
                            <span className='w-8-px h-8-px bg-success-main rounded-circle position-absolute end-0 bottom-0' />
                          </span>
                          <div>
                            <h6 className='text-md fw-semibold mb-4'>
                              Kathryn Murphy
                            </h6>
                            <p className='mb-0 text-sm text-secondary-light text-w-100-px'>
                              hey! there i’m...
                            </p>
                          </div>
                        </div>
                        <div className='d-flex flex-column align-items-end'>
                          <span className='text-sm text-secondary-light flex-shrink-0'>
                            12:30 PM
                          </span>
                          <span className='mt-4 text-xs text-base w-16-px h-16-px d-flex justify-content-center align-items-center bg-primary rounded-circle'>
                            8
                          </span>
                        </div>
                      </Link>
                      <Link
                        to='#'
                        className='px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between'
                      >
                        <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                          <span className='w-40-px h-40-px rounded-circle flex-shrink-0 position-relative'>
                            <img
                              src='/assets/images/notification/profile-4.png'
                              alt=''
                            />
                            <span className='w-8-px h-8-px  bg-neutral-300 rounded-circle position-absolute end-0 bottom-0' />
                          </span>
                          <div>
                            <h6 className='text-md fw-semibold mb-4'>
                              Kathryn Murphy
                            </h6>
                            <p className='mb-0 text-sm text-secondary-light text-w-100-px'>
                              hey! there i’m...
                            </p>
                          </div>
                        </div>
                        <div className='d-flex flex-column align-items-end'>
                          <span className='text-sm text-secondary-light flex-shrink-0'>
                            12:30 PM
                          </span>
                          <span className='mt-4 text-xs text-base w-16-px h-16-px d-flex justify-content-center align-items-center bg-primary rounded-circle'>
                            2
                          </span>
                        </div>
                      </Link>
                      <Link
                        to='#'
                        className='px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between bg-neutral-50'
                      >
                        <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                          <span className='w-40-px h-40-px rounded-circle flex-shrink-0 position-relative'>
                            <img
                              src='/assets/images/notification/profile-5.png'
                              alt=''
                            />
                            <span className='w-8-px h-8-px bg-primary rounded-circle position-absolute end-0 bottom-0' />
                          </span>
                          <div>
                            <h6 className='text-md fw-semibold mb-4'>
                              Kathryn Murphy
                            </h6>
                            <p className='mb-0 text-sm text-secondary-light text-w-100-px'>
                              hey! there i’m...
                            </p>
                          </div>
                        </div>
                        <div className='d-flex flex-column align-items-end'>
                          <span className='text-sm text-secondary-light flex-shrink-0'>
                            12:30 PM
                          </span>
                          <span className='mt-4 text-xs text-base w-16-px h-16-px d-flex justify-content-center align-items-center bg-neutral-400 rounded-circle'>
                            0
                          </span>
                        </div>
                      </Link>
                      <Link
                        to='#'
                        className='px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between bg-neutral-50'
                      >
                        <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                          <span className='w-40-px h-40-px rounded-circle flex-shrink-0 position-relative'>
                            <img
                              src='/assets/images/notification/profile-6.png'
                              alt=''
                            />
                            <span className='w-8-px h-8-px bg-neutral-300 rounded-circle position-absolute end-0 bottom-0' />
                          </span>
                          <div>
                            <h6 className='text-md fw-semibold mb-4'>
                              Kathryn Murphy
                            </h6>
                            <p className='mb-0 text-sm text-secondary-light text-w-100-px'>
                              hey! there i’m...
                            </p>
                          </div>
                        </div>
                        <div className='d-flex flex-column align-items-end'>
                          <span className='text-sm text-secondary-light flex-shrink-0'>
                            12:30 PM
                          </span>
                          <span className='mt-4 text-xs text-base w-16-px h-16-px d-flex justify-content-center align-items-center bg-neutral-400 rounded-circle'>
                            0
                          </span>
                        </div>
                      </Link>
                      <Link
                        to='#'
                        className='px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between'
                      >
                        <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                          <span className='w-40-px h-40-px rounded-circle flex-shrink-0 position-relative'>
                            <img
                              src='/assets/images/notification/profile-7.png'
                              alt=''
                            />
                            <span className='w-8-px h-8-px bg-success-main rounded-circle position-absolute end-0 bottom-0' />
                          </span>
                          <div>
                            <h6 className='text-md fw-semibold mb-4'>
                              Kathryn Murphy
                            </h6>
                            <p className='mb-0 text-sm text-secondary-light text-w-100-px'>
                              hey! there i’m...
                            </p>
                          </div>
                        </div>
                        <div className='d-flex flex-column align-items-end'>
                          <span className='text-sm text-secondary-light flex-shrink-0'>
                            12:30 PM
                          </span>
                          <span className='mt-4 text-xs text-base w-16-px h-16-px d-flex justify-content-center align-items-center bg-primary rounded-circle'>
                            8
                          </span>
                        </div>
                      </Link>
                    </div>
                    <div className='text-center py-12 px-16'>
                      <Link
                        to='#'
                        className='text-primary-600 fw-semibold text-md'
                      >
                        See All Message
                      </Link>
                    </div>
                  </div>
                </div>
                {/* Message dropdown end */}
                {/* Notification dropdown start */}
                <div className='dropdown'>
                  <button
                    className='has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <Icon
                      icon='iconoir:bell'
                      className='text-primary-light text-xl'
                    />
                    {unreadCount > 0 && (
                      <span className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'>
                        {unreadCount}
                        <span className='visually-hidden'>unread messages</span>
                      </span>
                    )}
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-lg p-0'>
                    <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-0'>
                          Notifications
                        </h6>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                         <span className='text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center'>
                          {unreadCount}
                        </span>
                        {unreadCount > 0 && (
                          <button 
                            onClick={handleMarkAllRead}
                            className="btn btn-sm btn-outline-primary"
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          >
                            Mark Read
                          </button>
                        )}
                      </div>
                    </div>
                    <div className='max-h-400-px overflow-y-auto scroll-sm pe-4'>
                      {notifications.length === 0 ? (
                        <div className="p-3 text-center text-secondary-light">
                          No notifications
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between ${notif.is_read ? '' : 'bg-neutral-50'}`}
                          >
                            <div className='text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'>
                              <span className={`w-44-px h-44-px rounded-circle d-flex justify-content-center align-items-center flex-shrink-0 ${notif.is_read ? 'bg-neutral-200 text-secondary-light' : 'bg-success-subtle text-success-main'}`}>
                                <Icon
                                  icon='bitcoin-icons:verify-outline'
                                  className='icon text-xxl'
                                />
                              </span>
                              <div>
                                <h6 className='text-md fw-semibold mb-4'>
                                  {notif.title}
                                </h6>
                                <p className='mb-0 text-sm text-secondary-light text-w-200-px'>
                                  {notif.body}
                                </p>
                              </div>
                            </div>
                            <span className='text-sm text-secondary-light flex-shrink-0'>
                              {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                    <div className='text-center py-12 px-16'>
                      <Link
                        to='#'
                        className='text-primary-600 fw-semibold text-md'
                      >
                        See All Notification
                      </Link>
                    </div>
                  </div>
                </div>
                {/* Notification dropdown end */}
                <div className='dropdown'>
                  <button
                    className='d-flex justify-content-center align-items-center rounded-circle'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <img
                      src={
                        user?.profile_image 
                          ? (user.profile_image.startsWith('http') 
                              ? user.profile_image 
                              : user.profile_image.includes('/avatar/') 
                                ? `${config.API_BASE_URL}${user.profile_image.startsWith('/') ? '' : '/'}${user.profile_image}`
                                : (user.profile_image.startsWith('/') ? user.profile_image : `/${user.profile_image}`))
                          : '/assets/images/user.png'
                      }
                      alt='image_user'
                      className='w-40-px h-40-px object-fit-cover rounded-circle'
                      onError={(e) => e.target.src = '/assets/images/user.png'}
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm'>
                    <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                          {user?.full_name || 'User'}
                        </h6>
                        <span className='text-secondary-light fw-medium text-sm'>
                          {user?.role || 'Guest'}
                        </span>
                      </div>
                      <button type='button' className='hover-text-danger'>
                        <Icon
                          icon='radix-icons:cross-1'
                          className='icon text-xl'
                        />
                      </button>
                    </div>
                    <ul className='to-top-list'>
                      <li>
                        <Link
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'
                          to={user ? `/view-profile/${user.id}` : '/view-profile'}
                        >
                          <Icon
                            icon='solar:user-linear'
                            className='icon text-xl'
                          />{" "}
                          My Profile
                        </Link>
                      </li>
                      
                      <li>
                        <Link
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                          to='#'
                          onClick={(e) => {
                            e.preventDefault();
                            auth.logout();
                          }}
                        >
                          <Icon icon='lucide:power' className='icon text-xl' />{" "}
                          Log Out
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className='dashboard-main-body'>{children}</div>

        {/* Footer section */}
        <footer className='d-footer'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <p className='mb-0'>© 2026 Vscroll. All Rights Reserved.</p>
            </div>
            <div className='col-auto'>
              <p className='mb-0'>
                Made by <span className='text-primary-600'>Vscroll</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
